import { computed, ref, watch } from 'vue'

/** Where the financed property comes from — it decides how many steps follow. */
export type FinancingOrigin = 'new' | 'portfolio'

export type ResumeStep = 'done' | null

export const ORIGIN_STEP = 1
export const BORROWER_STEP = 2
export const TYPE_STEP = 3
export const LOCATION_STEP = 4
export const UNITS_STEP = 5
export const FINANCING_STEP = 6
export const DOCUMENTS_STEP = 7
export const NEW_PROPERTY_DONE_STEP = 8
export const PORTFOLIO_FINANCING_STEP = 2
export const PORTFOLIO_DOCUMENTS_STEP = 3
export const PORTFOLIO_DONE_STEP = 4

const stepOfField: Record<string, number> = {
  estate_object_id: ORIGIN_STEP,
  buyer_type: BORROWER_STEP,
  buyer_id: BORROWER_STEP,
  estate_object_type: TYPE_STEP,
  google_place_id: LOCATION_STEP,
  street: LOCATION_STEP,
  house_number: LOCATION_STEP,
  address_addition: LOCATION_STEP,
  zip_code: LOCATION_STEP,
  city: LOCATION_STEP,
  state: LOCATION_STEP,
  living_space_size: UNITS_STEP,
  business_space_size: UNITS_STEP,
  number_of_units: UNITS_STEP,
  actual_rent_living: UNITS_STEP,
  actual_rent_business: UNITS_STEP,
  expected_rent_living: UNITS_STEP,
  expected_rent_business: UNITS_STEP,
  purchase_price: FINANCING_STEP,
  land_register_charge: FINANCING_STEP,
  broker_commission: FINANCING_STEP,
  notary_land_registry_fees: FINANCING_STEP,
  equity: FINANCING_STEP,
  loan_type: FINANCING_STEP,
  total_financing_amount: FINANCING_STEP,
  interest_term_years: FINANCING_STEP,
  repayment_rate: FINANCING_STEP,
  documents: DOCUMENTS_STEP,
}

/** The portfolio run asks for the object and the conditions — nothing else can be rejected. */
const portfolioStepOfField: Record<string, number> = {
  estate_object_id: ORIGIN_STEP,
}

const stepOfError = (field: string, origin: FinancingOrigin | null): number => {
  const key = field.split('.')[0]

  if (origin === 'portfolio') {
    return portfolioStepOfField[key] ?? PORTFOLIO_FINANCING_STEP
  }

  return stepOfField[key] ?? FINANCING_STEP
}

/** What the current step asks for. The two runs share step numbers, the screens tell them apart. */
export type WizardScreen =
  'origin' | 'borrower' | 'type' | 'location' | 'units' | 'financing' | 'conditions' | 'documents' | 'done'

const newPropertyScreens: Record<number, WizardScreen> = {
  [ORIGIN_STEP]: 'origin',
  [BORROWER_STEP]: 'borrower',
  [TYPE_STEP]: 'type',
  [LOCATION_STEP]: 'location',
  [UNITS_STEP]: 'units',
  [FINANCING_STEP]: 'financing',
  [DOCUMENTS_STEP]: 'documents',
}

const portfolioScreens: Record<number, WizardScreen> = {
  [ORIGIN_STEP]: 'origin',
  [PORTFOLIO_FINANCING_STEP]: 'conditions',
  [PORTFOLIO_DOCUMENTS_STEP]: 'documents',
}

const scrollToTop = (): void => window.scrollTo({ top: 0, behavior: 'smooth' })

const doneStepOf = (origin: FinancingOrigin | null): number =>
  origin === 'portfolio' ? PORTFOLIO_DONE_STEP : NEW_PROPERTY_DONE_STEP

/**
 * Wer die Absicht schon auf der Startseite gewählt hat, beantwortet den ersten Schritt nicht
 * zweimal. Beim Bestand bleibt er stehen – dort fehlt auf demselben Schritt noch das Objekt.
 */
const stepOfInitialOrigin = (origin: FinancingOrigin | null): number => (origin === 'new' ? BORROWER_STEP : ORIGIN_STEP)

export function useFinancingRequestCreateSteps(
  resume: () => ResumeStep,
  createdProjectId: () => string | undefined,
  errors: () => Partial<Record<string, string>>,
  initialOrigin: () => FinancingOrigin | null,
) {
  const origin = ref<FinancingOrigin | null>(initialOrigin())
  const step = ref<number>(resume() === 'done' ? doneStepOf(origin.value) : stepOfInitialOrigin(origin.value))

  /** Missing answers stay quiet until the investor tries to move on. */
  const highlightMissing = ref(false)

  /** Once the request exists the run is over — no going back into the answers. */
  const locked = computed(() => Boolean(createdProjectId()))

  const doneStep = computed(() => doneStepOf(origin.value))

  const screen = computed<WizardScreen>(
    () => (origin.value === 'portfolio' ? portfolioScreens : newPropertyScreens)[step.value] ?? 'done',
  )

  const stepErrors = computed(() =>
    Object.entries(errors())
      .filter(([field]) => stepOfError(field, origin.value) === step.value)
      .map(([, message]) => message)
      .filter((message): message is string => !!message),
  )

  /** Die Antworten überleben das Einreichen – erst die Antwort des Servers schaltet auf den Abschluss. */
  watch(resume, (value) => {
    if (value !== 'done') {
      return
    }

    step.value = doneStep.value
    highlightMissing.value = false
  })

  function selectOrigin(next: FinancingOrigin): void {
    if (locked.value) {
      return
    }

    origin.value = next
  }

  function canReturnTo(target: number): boolean {
    return !locked.value && target >= ORIGIN_STEP && target < step.value
  }

  function goBackTo(target: number): void {
    if (!canReturnTo(target)) {
      return
    }

    step.value = target
    highlightMissing.value = false
    scrollToTop()
  }

  function advance(): void {
    step.value = Math.min(step.value + 1, doneStep.value)
    highlightMissing.value = false
    scrollToTop()
  }

  function requestValidation(): void {
    highlightMissing.value = true
  }

  function showFirstRejectedStep(rejected: Record<string, string>): void {
    const steps = Object.keys(rejected).map((field) => stepOfError(field, origin.value))

    step.value = steps.length ? Math.min(...steps) : step.value
    highlightMissing.value = true
  }

  return {
    origin,
    step,
    screen,
    locked,
    doneStep,
    stepErrors,
    highlightMissing,
    requestValidation,
    selectOrigin,
    canReturnTo,
    goBackTo,
    advance,
    showFirstRejectedStep,
  }
}
